import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { Form, FormikProvider, useFormik } from "formik";
import { Icon } from "@iconify/react";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { LoadingButton } from "@mui/lab";
import { BaseTable } from "../../BaseTable/BaseTable";
import MuiConfirmationDialog from "../../@material-extend/MuiConfirmationDialog";
import { useAuth } from "src/hooks/useAuth.hook";
import { useAccess } from "src/hooks/useAccess.hook";
import { useList, useMutate } from "src/hooks/useFetch.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { selector } from "src/react-query/select";
import { alertSlice } from "src/store/alert/alert.slice";
import enums, { FEATURE } from "src/util/enums";
import { fDateTime } from "src/utils/formatTime";
import sanitizeValues from "src/utils/sanitizeValues";
import {
  getTeamUrl,
  fleetTariffsUrl,
  fleetTariffIdUrl,
} from "src/react-query/endPoints";
import {
  TARIFF_TYPES,
  TARIFF_TYPE_OPTIONS,
  TARIFF_TYPE_LABELS,
  CHARGER_POWER_TYPE_OPTIONS,
  FLEET_ENTITY_STATUS_OPTIONS,
  fleetTariffValidationSchema,
  getTariffDefaultValues,
  getEmptySlabRow,
  toList,
  statusChipColor,
  formatMoney,
} from "../fleet.constants";

const getTariffId = (row) => row?.tariff_plan_id ?? row?.tariffPlanId ?? row?.id;

const toDateOnly = (value) => (value ? String(value).slice(0, 10) : "");

const priceSummary = (row) => {
  const currency = row?.currency;
  switch (row?.tariff_type) {
    case TARIFF_TYPES.FLAT:
      return `${formatMoney(row?.flat_price_per_unit, currency)} / kWh`;
    case TARIFF_TYPES.AC_DC:
      return `AC ${formatMoney(row?.ac_price_per_unit, currency)} · DC ${formatMoney(
        row?.dc_price_per_unit,
        currency
      )}`;
    case TARIFF_TYPES.SLAB:
      return "Slab based";
    default:
      return "-";
  }
};

const TariffsTab = ({ operatorId }) => {
  const dispatch = useDispatch();
  const { teamId } = useSelectedTeam();
  const { user } = useAuth();
  const isScopedOperator = user?.company?.fleetOperatorScope != null;
  const hasCreateAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "C");
  const hasUpdateAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "U");
  const hasDeleteAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "D");

  const {
    data: tariffsData,
    isFetching,
  } = useList("fleetTariffs", undefined, operatorId);
  const tariffs = useMemo(() => toList(tariffsData), [tariffsData]);

  const { mutate, isLoading } = useMutate("fleetTariffs", { id: operatorId });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTariffId, setEditingTariffId] = useState(null);
  const [deletingTariff, setDeletingTariff] = useState(null);

  // Slabs usually only come with the tariff detail, not the list row.
  const { data: tariffDetail, isFetching: isDetailFetching } = useQuery(
    queryKeys.fleetTariff(teamId, operatorId, editingTariffId),
    fetchWithError,
    {
      enabled: Boolean(teamId && operatorId && editingTariffId),
      select: selector,
    }
  );

  const openCreate = () => {
    setEditingTariffId(null);
    formik.resetForm({ values: getTariffDefaultValues() });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingTariffId(getTariffId(row));
    formik.resetForm({ values: getTariffDefaultValues() });
    setDialogOpen(true);
  };

  const validateSlabs = (slabs) => {
    if (!slabs.length) return "Add at least one slab row";
    for (let i = 0; i < slabs.length; i += 1) {
      const slab = slabs[i];
      const min = Number(slab.min_units);
      const price = Number(slab.price_per_unit);
      if (slab.price_per_unit === "" || Number.isNaN(price) || price < 0) {
        return `Slab ${i + 1}: price per kWh is required`;
      }
      if (Number.isNaN(min) || min < 0) {
        return `Slab ${i + 1}: from-units must be a non-negative number`;
      }
      if (slab.max_units !== "" && slab.max_units !== null) {
        const max = Number(slab.max_units);
        if (Number.isNaN(max) || max <= min) {
          return `Slab ${i + 1}: to-units must be greater than from-units`;
        }
      } else if (i !== slabs.length - 1) {
        return `Slab ${i + 1}: only the last slab can be open-ended`;
      }
    }
    return null;
  };

  const onSubmit = (values, { setSubmitting }) => {
    const sanitized = sanitizeValues(values);
    const { slabs, ...plan } = sanitized;
    const body = {
      name: plan.name,
      tariff_type: plan.tariff_type,
      currency: plan.currency || "INR",
      tax_percent: plan.tax_percent === "" ? 0 : Number(plan.tax_percent),
      flat_price_per_unit:
        plan.tariff_type === TARIFF_TYPES.FLAT && plan.flat_price_per_unit !== ""
          ? Number(plan.flat_price_per_unit)
          : null,
      ac_price_per_unit:
        plan.tariff_type === TARIFF_TYPES.AC_DC && plan.ac_price_per_unit !== ""
          ? Number(plan.ac_price_per_unit)
          : null,
      dc_price_per_unit:
        plan.tariff_type === TARIFF_TYPES.AC_DC && plan.dc_price_per_unit !== ""
          ? Number(plan.dc_price_per_unit)
          : null,
      effective_from: plan.effective_from || null,
      effective_to: plan.effective_to || null,
      status: plan.status,
    };
    if (plan.tariff_type === TARIFF_TYPES.SLAB) {
      const slabError = validateSlabs(slabs);
      if (slabError) {
        dispatch(
          alertSlice.actions.setAlert({
            open: true,
            message: slabError,
            severity: enums.SEVERITY.ERROR,
          })
        );
        setSubmitting(false);
        return;
      }
      body.slabs = slabs.map((slab, index) => ({
        min_units: Number(slab.min_units),
        max_units:
          slab.max_units === "" || slab.max_units === null
            ? null
            : Number(slab.max_units),
        price_per_unit: Number(slab.price_per_unit),
        charger_power_type: slab.charger_power_type || null,
        sort_order: index,
      }));
    }
    const isEdit = Boolean(editingTariffId);
    mutate(
      {
        url: isEdit
          ? getTeamUrl(teamId, fleetTariffIdUrl(operatorId, editingTariffId))
          : getTeamUrl(teamId, fleetTariffsUrl(operatorId)),
        method: isEdit ? "PUT" : "POST",
        body,
      },
      {
        onSuccess: () => {
          setDialogOpen(false);
          setEditingTariffId(null);
        },
      }
    );
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: getTariffDefaultValues(),
    validationSchema: fleetTariffValidationSchema,
    onSubmit,
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    resetForm,
  } = formik;

  // Prefill form when editing and the detail arrives.
  useEffect(() => {
    if (dialogOpen && editingTariffId && tariffDetail) {
      const detail = tariffDetail?.tariff ?? tariffDetail;
      const slabs = toList(tariffDetail?.slabs ?? detail?.slabs).map(
        (slab, index) => ({
          min_units: slab?.min_units ?? 0,
          max_units: slab?.max_units ?? "",
          price_per_unit: slab?.price_per_unit ?? "",
          charger_power_type: slab?.charger_power_type ?? "",
          sort_order: slab?.sort_order ?? index,
        })
      );
      resetForm({
        values: {
          ...getTariffDefaultValues(),
          name: detail?.name ?? "",
          tariff_type: detail?.tariff_type ?? TARIFF_TYPES.FLAT,
          currency: detail?.currency ?? "INR",
          tax_percent: detail?.tax_percent ?? 0,
          flat_price_per_unit: detail?.flat_price_per_unit ?? "",
          ac_price_per_unit: detail?.ac_price_per_unit ?? "",
          dc_price_per_unit: detail?.dc_price_per_unit ?? "",
          effective_from: toDateOnly(detail?.effective_from),
          effective_to: toDateOnly(detail?.effective_to),
          status: detail?.status ?? "active",
          slabs,
        },
      });
    }
  }, [dialogOpen, editingTariffId, tariffDetail, resetForm]);

  // Ensure a starting slab row exists when switching to SLAB type.
  useEffect(() => {
    if (
      dialogOpen &&
      values.tariff_type === TARIFF_TYPES.SLAB &&
      !values.slabs.length
    ) {
      setFieldValue("slabs", [getEmptySlabRow(0, 0)]);
    }
  }, [dialogOpen, values.tariff_type, values.slabs.length, setFieldValue]);

  const onDeleteTariff = () => {
    mutate(
      {
        url: getTeamUrl(
          teamId,
          fleetTariffIdUrl(operatorId, getTariffId(deletingTariff))
        ),
        method: "DELETE",
      },
      {
        onSuccess: () => {
          setDeletingTariff(null);
        },
      }
    );
  };

  const addSlabRow = () => {
    const slabs = [...values.slabs];
    const last = slabs[slabs.length - 1];
    const nextMin =
      last && last.max_units !== "" && last.max_units !== null
        ? Number(last.max_units)
        : "";
    slabs.push(getEmptySlabRow(nextMin === "" ? "" : nextMin, slabs.length));
    setFieldValue("slabs", slabs);
  };

  const removeSlabRow = (index) => {
    const slabs = values.slabs
      .filter((slab, i) => i !== index)
      .map((slab, i) => ({ ...slab, sort_order: i }));
    setFieldValue("slabs", slabs);
  };

  const updateSlabRow = (index, field, value) => {
    const slabs = values.slabs.map((slab, i) =>
      i === index ? { ...slab, [field]: value } : slab
    );
    // Chain min_units of the next row from this row's max_units.
    if (field === "max_units" && index < slabs.length - 1) {
      slabs[index + 1] = {
        ...slabs[index + 1],
        min_units: value === "" ? "" : Number(value),
      };
    }
    setFieldValue("slabs", slabs);
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
      },
      {
        field: "tariff_type",
        headerName: "Type",
        flex: 0.6,
        renderCell: ({ value }) => TARIFF_TYPE_LABELS[value] ?? value ?? "-",
      },
      {
        field: "price",
        headerName: "Pricing",
        flex: 1.2,
        sortable: false,
        renderCell: ({ row }) => priceSummary(row),
      },
      {
        field: "tax_percent",
        headerName: "Tax %",
        flex: 0.5,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "effective_from",
        headerName: "Effective From",
        flex: 0.8,
        renderCell: ({ value }) => (value ? fDateTime(value, "dd MMM yyyy") : "-"),
      },
      {
        field: "effective_to",
        headerName: "Effective To",
        flex: 0.8,
        renderCell: ({ value }) => (value ? fDateTime(value, "dd MMM yyyy") : "-"),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.6,
        renderCell: ({ value }) => (
          <Chip size="small" label={value ?? "-"} color={statusChipColor(value)} />
        ),
      },
      {
        field: "Actions",
        headerName: "Actions",
        width: 110,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row">
            {hasUpdateAccess && !isScopedOperator && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => openEdit(row)}>
                  <Icon icon="eva:edit-outline" />
                </IconButton>
              </Tooltip>
            )}
            {hasDeleteAccess && !isScopedOperator && (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => setDeletingTariff(row)}>
                  <Icon icon="eva:trash-2-outline" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasUpdateAccess, hasDeleteAccess, isScopedOperator]
  );

  const dialogTextField = (name, label, extra = {}) => (
    <FormControl fullWidth size="small">
      <TextField
        size="small"
        label={label}
        {...getFieldProps(name)}
        error={Boolean(touched[name] && errors[name])}
        helperText={touched[name] && errors[name]}
        {...extra}
      />
    </FormControl>
  );

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Tariff Plans</Typography>
        {hasCreateAccess && !isScopedOperator && (
          <Button
            variant="contained"
            startIcon={<Icon icon="eva:plus-fill" />}
            onClick={openCreate}
          >
            Add Tariff
          </Button>
        )}
      </Stack>
      <BaseTable
        rows={tariffs}
        columns={columns}
        loading={isFetching}
        getRowId={(row) => getTariffId(row)}
        pageSize={10}
      />
      {!isFetching && !tariffs.length && (
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ py: 2 }}
        >
          No tariff plans configured yet. Sessions fall back to charger pricing.
        </Typography>
      )}

      {/* Create / edit tariff dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingTariffId(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingTariffId ? "Edit Tariff Plan" : "Add Tariff Plan"}
        </DialogTitle>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={2} mt={0}>
                <Grid item xs={12} sm={6}>
                  {dialogTextField("name", "Name", { required: true })}
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="tariff-type">Tariff Type</InputLabel>
                    <Select
                      labelId="tariff-type"
                      label="Tariff Type"
                      {...getFieldProps("tariff_type")}
                    >
                      {TARIFF_TYPE_OPTIONS.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="tariff-status">Status</InputLabel>
                    <Select
                      labelId="tariff-status"
                      label="Status"
                      {...getFieldProps("status")}
                    >
                      {FLEET_ENTITY_STATUS_OPTIONS.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  {dialogTextField("currency", "Currency")}
                </Grid>
                <Grid item xs={12} sm={3}>
                  {dialogTextField("tax_percent", "Tax %", {
                    type: "number",
                    inputProps: { min: 0, max: 100, step: "0.001" },
                  })}
                </Grid>
                <Grid item xs={12} sm={3}>
                  {dialogTextField("effective_from", "Effective From", {
                    type: "date",
                    InputLabelProps: { shrink: true },
                  })}
                </Grid>
                <Grid item xs={12} sm={3}>
                  {dialogTextField("effective_to", "Effective To", {
                    type: "date",
                    InputLabelProps: { shrink: true },
                  })}
                </Grid>

                {values.tariff_type === TARIFF_TYPES.FLAT && (
                  <Grid item xs={12} sm={4}>
                    {dialogTextField("flat_price_per_unit", "Price per kWh", {
                      type: "number",
                      required: true,
                      inputProps: { min: 0, step: "0.0001" },
                    })}
                  </Grid>
                )}
                {values.tariff_type === TARIFF_TYPES.AC_DC && (
                  <>
                    <Grid item xs={12} sm={4}>
                      {dialogTextField("ac_price_per_unit", "AC Price per kWh", {
                        type: "number",
                        required: true,
                        inputProps: { min: 0, step: "0.0001" },
                      })}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      {dialogTextField("dc_price_per_unit", "DC Price per kWh", {
                        type: "number",
                        required: true,
                        inputProps: { min: 0, step: "0.0001" },
                      })}
                    </Grid>
                  </>
                )}
              </Grid>

              {values.tariff_type === TARIFF_TYPES.SLAB && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography variant="subtitle1">Slabs</Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Icon icon="eva:plus-fill" />}
                      onClick={addSlabRow}
                    >
                      Add Slab
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Slabs are graduated: each range is billed at its own price.
                    Leave &quot;To (kWh)&quot; empty on the last row for an
                    unlimited upper bound.
                  </Typography>
                  {isDetailFetching && editingTariffId ? (
                    <Typography variant="body2" sx={{ py: 2 }}>
                      Loading slabs...
                    </Typography>
                  ) : (
                    values.slabs.map((slab, index) => (
                      <Grid
                        container
                        spacing={1}
                        key={`slab-${index}`}
                        alignItems="center"
                        mt={0.5}
                      >
                        <Grid item xs={6} sm={2.5}>
                          <TextField
                            size="small"
                            fullWidth
                            label="From (kWh)"
                            type="number"
                            inputProps={{ min: 0, step: "0.001" }}
                            value={slab.min_units}
                            onChange={(e) =>
                              updateSlabRow(index, "min_units", e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={6} sm={2.5}>
                          <TextField
                            size="small"
                            fullWidth
                            label="To (kWh)"
                            type="number"
                            inputProps={{ min: 0, step: "0.001" }}
                            placeholder="Unlimited"
                            InputLabelProps={{ shrink: true }}
                            value={slab.max_units ?? ""}
                            onChange={(e) =>
                              updateSlabRow(index, "max_units", e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            size="small"
                            fullWidth
                            required
                            label="Price per kWh"
                            type="number"
                            inputProps={{ min: 0, step: "0.0001" }}
                            value={slab.price_per_unit}
                            onChange={(e) =>
                              updateSlabRow(index, "price_per_unit", e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={6} sm={2.5}>
                          <FormControl fullWidth size="small">
                            <InputLabel id={`slab-power-${index}`}>
                              AC/DC
                            </InputLabel>
                            <Select
                              labelId={`slab-power-${index}`}
                              label="AC/DC"
                              value={slab.charger_power_type ?? ""}
                              onChange={(e) =>
                                updateSlabRow(
                                  index,
                                  "charger_power_type",
                                  e.target.value
                                )
                              }
                            >
                              {CHARGER_POWER_TYPE_OPTIONS.map(
                                ({ value, label }) => (
                                  <MenuItem key={value || "any"} value={value}>
                                    {label}
                                  </MenuItem>
                                )
                              )}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={1.5}>
                          <Tooltip title="Remove Slab">
                            <span>
                              <IconButton
                                size="small"
                                disabled={values.slabs.length <= 1}
                                onClick={() => removeSlabRow(index)}
                              >
                                <Icon icon="eva:trash-2-outline" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    ))
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setDialogOpen(false);
                  setEditingTariffId(null);
                }}
              >
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isLoading}
              >
                Save
              </LoadingButton>
            </DialogActions>
          </Form>
        </FormikProvider>
      </Dialog>

      {/* Delete tariff confirm */}
      <MuiConfirmationDialog
        open={Boolean(deletingTariff)}
        onClose={() => setDeletingTariff(null)}
        title="Delete Tariff Plan"
        severity="error"
        contentText={`Are you sure you want to delete tariff "${
          deletingTariff?.name ?? ""
        }"?`}
        closeText="Cancel"
        okText="Delete"
        onOk={onDeleteTariff}
        isLoading={isLoading}
      />
    </>
  );
};

export default TariffsTab;
